import { TRACK_NESTING } from '@disco/constants/FeatureTypes'
import {
  alterPlaylistGroups,
  alterTrackGroups,
} from '@disco/state-management/actions/ExplorerActions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'

import React, { useCallback, useEffect } from 'react'
import { useDispatch } from 'hooks/use-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import type { Group } from 'types/group'
import type { Playlist } from 'types/playlist'
import type { Track } from 'types/track'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Heading from 'components/ui/heading'
import ModalWindow from 'components/ui/modal-window'

const getMessage = (type: 'playlist' | 'track'): string | null | undefined => {
  if (type === 'playlist') {
    return 'This playlist has tracks that include stems. If you’d like to import the stems, click the button below.'
  }

  if (type === 'track') {
    return 'This track has stems. If you’d like to import the stems, click the button below.'
  }
}

type PlaylistState = {
  type: 'playlist'
  playlists: Playlist[]
  addGroups: Group[]
  removeGroups?: Group[]
  options?: {
    alterTracks?: boolean
    disableNotifications?: boolean
  }
}
type TrackState = {
  type: 'track'
  tracks: Track[]
  addGroups: Group[]
  removeGroups?: Group[]
  options?: {
    disableNotifications?: boolean
  }
}

const ImportStemsModal = (): null | React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as PlaylistState | TrackState | null
  const isNestingEnabled = useFeatureEnabled(TRACK_NESTING)
  useEffect(() => {
    if (!isNestingEnabled || !state) {
      navigate('/', { replace: true })
    }
  }, [isNestingEnabled, state])
  const handleSubmit = useCallback(
    (includeStems: boolean) => {
      if (state.type === 'playlist') {
        const { playlists, addGroups, removeGroups, options } = state
        dispatch(
          alterPlaylistGroups(playlists, addGroups, removeGroups, {
            disableNotifications: options?.disableNotifications,
            alterTracks: options?.alterTracks,
            includeStems,
          }),
        )
      }

      if (state.type === 'track') {
        const { tracks, addGroups, removeGroups, options } = state
        dispatch(
          alterTrackGroups(tracks, addGroups, removeGroups, {
            disableNotifications: options?.disableNotifications,
            includeStems,
          }),
        )
      }

      navigate('/')
    },
    [dispatch, state],
  )
  const handleCancel = useCallback(() => {
    navigate('/')
  }, [navigate])

  if (!state) {
    return null
  }

  return (
    <ModalWindow
      header={<Heading>Import stems</Heading>}
      actions={
        <ButtonContainer>
          <Button
            caption="Don’t import stems"
            onClick={() => handleSubmit(false)}
          />
          <Button
            appearance="primary"
            caption="Import stems"
            onClick={() => handleSubmit(true)}
          />
        </ButtonContainer>
      }
      isOpen={true}
      width="small"
      onClose={handleCancel}
    >
      <div className="form-note">{getMessage(state.type)}</div>
    </ModalWindow>
  )
}

export default ImportStemsModal
